import React, { useState } from 'react';
import styled from 'styled-components';
import { BackgroundColor, BorderColor, TextColor } from 'styles/Global/colorSemantic';
import { Radius } from 'styles/Global/objectStyles';
import { Body5Regular } from 'styles/Global/typography';
import { GetVoucherListApiListType } from 'models/Admin/AdminVoucherPageTypes';
import { ModalBackground, ModalContent, ModalFooter, ModalLayout, ModalTitle } from 'components/Modal/ModalForms';
import BasicButton from 'components/Buttons/BasicButton';
import { CloseButtonWrapper } from 'styles/Modal/modalStyle';
import ModalDeleteIcon from 'assets/Icons/DeleteIcon/CloseDialogue';
import { commonText } from 'constants/commonConstants';
import BasicConfirmModal from 'components/Modal/BasicConfirmModal';

interface VoucherSmsModalProps {
    toggle: () => void;
    selectedVoucherData: GetVoucherListApiListType | null;
    onSendSms: (code: string, mobile: string, name: string, description: string) => void;
}

const VoucherSmsModal: React.FC<VoucherSmsModalProps> = ({ toggle, selectedVoucherData, onSendSms }) => {
    const [mobile, setMobile] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

    const handleSendSms = () => {
        if (selectedVoucherData && mobile.trim() && name.trim() && description.trim()) {
            onSendSms(selectedVoucherData.code, mobile, name, description);
            setIsConfirmModalOpen(false);
            toggle();
        }
    };

    const handleConfirmSendSms = () => {
        setIsConfirmModalOpen(true);
    };

    const isFormValid = mobile.trim() && name.trim() && description.trim();

    return (
        <>
            <ModalBackground>
                <ModalLayout $width='520px'>
                    <ModalInnerLayout>
                        <ModalTitleAndContentContainer>
                            <ModalTitle>SMS 전송</ModalTitle>
                            <ModalContent>바우처 코드에 대한 SMS를 전송합니다.</ModalContent>
                        </ModalTitleAndContentContainer>
                        <SmsFormLayout>
                            <SmsFormContainer>
                                <InputGroup>
                                    <Label>바우처 코드</Label>
                                    <Input value={selectedVoucherData?.code || ''} disabled placeholder='바우처 코드' />
                                </InputGroup>
                                <InputGroup>
                                    <Label>전화번호</Label>
                                    <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder='전화번호를 입력하세요' />
                                </InputGroup>
                                <InputGroup>
                                    <Label>고객명</Label>
                                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder='고객명을 입력하세요' />
                                </InputGroup>
                                <InputGroup>
                                    <Label>설명</Label>
                                    <TextArea value={description} onChange={(e) => setDescription(e.target.value)} placeholder='설명을 입력하세요' />
                                </InputGroup>
                            </SmsFormContainer>
                        </SmsFormLayout>
                    </ModalInnerLayout>
                    <ModalFooter>
                        <BasicButton $width='160px' $height='48px' onClick={toggle} $type='anotherFill'>
                            {commonText.cancel}
                        </BasicButton>
                        <BasicButton $width='160px' $height='48px' $type='fill' onClick={handleConfirmSendSms} disabled={!isFormValid}>
                            전송
                        </BasicButton>
                    </ModalFooter>
                    <CloseButtonWrapper onClick={toggle}>
                        <ModalDeleteIcon />
                    </CloseButtonWrapper>
                </ModalLayout>
            </ModalBackground>
            {isConfirmModalOpen && (
                <BasicConfirmModal
                    title='메시지 전송 확인'
                    content='입력하신 정보로 바우처 코드를 전송하시겠습니까?'
                    cancelButton={() => setIsConfirmModalOpen(false)}
                    confirmButton={handleSendSms}
                    firstButton='취소'
                    secondButton='전송'
                />
            )}
        </>
    );
};

export default VoucherSmsModal;

const ModalInnerLayout = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: 16px;
`;

const ModalTitleAndContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 12px;
`;

const SmsFormLayout = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 18px 20px;
    border: 1px solid ${BorderColor.color_border_neutral};
    border-radius: 4px;
    gap: 14px;
`;

const SmsFormContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 18px 20px;
    background-color: ${BackgroundColor.color_background_neutral};
    border-radius: ${Radius.radius_4};
    gap: 16px;
`;

const InputGroup = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 8px;
`;

const Label = styled(Body5Regular)`
    color: ${TextColor.color_text_light};
`;

const Input = styled.input`
    width: 100%;
    padding: 8px 12px;
    border: 1px solid ${BorderColor.color_border_neutral};
    border-radius: 4px;
    font-size: 14px;
    color: ${TextColor.color_text_normal};
`;

const TextArea = styled.textarea`
    width: 100%;
    height: 120px;
    padding: 8px 12px;
    border: 1px solid ${BorderColor.color_border_neutral};
    border-radius: 4px;
    resize: none;
    font-size: 14px;
    color: ${TextColor.color_text_normal};
`;
